import React from "react";
import { Select } from "antd";
import {
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  WarningOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { PoiDeliveredChart, AugmentationChart } from "./SizingCharts";

export default function StepProjectInput({
  projectValues,
  setProjectValues,
  containerOptions = [],
  pcsOptions = [],
  loadingDropdowns = false,
  step1Errors = {},
  setStep1Errors,
  step1Touched = {},
  setStep1Touched,
  validatePurchaseYears,
  handlePurchaseYearsChange,
  previewLoading = false,
  previewData = null,
  showPreview = false,
  setShowPreview,
  handlePreviewSizing,
  onSubmit,
  submitting = false,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-6 animate-fade-in">
      {/* Core Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <div className="flex flex-col gap-1 md:col-span-2">
          <label>SIMULATION NAME</label>
          <input
            type="text"
            value={projectValues.name || ""}
            onChange={(e) => setProjectValues({ ...projectValues, name: e.target.value })}
            placeholder="Enter Project Name..."
            required
          />
        </div>

        <div className="flex flex-col gap-1">
          <label>CONTAINER</label>
          <Select
            value={projectValues.container_batch || undefined}
            disabled={loadingDropdowns}
            onChange={(val) => setProjectValues({ ...projectValues, container_batch: val })}
            placeholder="Select Container..."
            className="bess-antd-select w-full"
          >
            {containerOptions.map((opt) => (
              <Select.Option key={opt.id} value={opt.id}>
                {opt.oem} - {parseFloat(opt.rated_capacity_mwh).toFixed(3)} MWh
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label>PCS</label>
          <Select
            value={projectValues.pcs || undefined}
            disabled={loadingDropdowns}
            onChange={(val) => setProjectValues({ ...projectValues, pcs: val })}
            placeholder="Select PCS System..."
            className="bess-antd-select w-full"
          >
            {pcsOptions.map((opt) => (
              <Select.Option key={opt.id} value={opt.id}>
                {opt.oem} - {opt.model}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label>SIMULATION TYPE</label>
          <Select
            value={projectValues.simulation_type}
            onChange={(val) => {
              setProjectValues({ ...projectValues, simulation_type: val });
              if (val !== "closed_loop") {
                if (setStep1Errors) setStep1Errors((prev) => ({ ...prev, purchase_years: null }));
              } else if (step1Touched.purchase_years && validatePurchaseYears) {
                const err = validatePurchaseYears({ ...projectValues, simulation_type: val });
                if (setStep1Errors) setStep1Errors((prev) => ({ ...prev, purchase_years: err }));
              }
            }}
            className="bess-antd-select w-full"
          >
            <Select.Option value="closed_loop">Manual Battery Augmentation</Select.Option>
            <Select.Option value="open_loop">Without Battery Augmentation</Select.Option>
            <Select.Option value="auto_closed_loop">Auto Battery Augmentation</Select.Option>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label>STORAGE DURATION (HOURS)</label>
          <input
            type="number"
            value={projectValues.operation_hour}
            onChange={(e) =>
              setProjectValues({
                ...projectValues,
                operation_hour: e.target.value === "" ? "" : Number(e.target.value),
              })
            }
            min="1"
            max="24"
            placeholder="4"
          />
        </div>
      </div>

      {/* Equipment Quantities */}
      {projectValues.simulation_type !== "auto_closed_loop" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="flex flex-col gap-1">
            <label>INITIAL CONTAINER QUANTITY (NOS)</label>
            <input
              type="number"
              value={projectValues.container_nos}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  container_nos: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="Enter initial container quantity..."
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>PCS QUANTITY (NOS)</label>
            <input
              type="number"
              value={projectValues.pcs_nos}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  pcs_nos: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="Enter PCS quantity..."
            />
          </div>
        </div>
      )}

      {/* Simulation Settings */}
      <div className="mt-8 space-y-4">
        <h4 className="form-section-title text-base font-bold text-slate-800 uppercase tracking-wider pb-1">
          Simulation Settings
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col gap-1">
            <label>START YEAR</label>
            <input
              type="number"
              value={projectValues.start_year}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  start_year: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="Enter Start Year (e.g. 2026)..."
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>SIMULATION TENURE (YEARS)</label>
            <input
              type="number"
              value={projectValues.simulation_tenure}
              onChange={(e) => {
                const val = e.target.value === "" ? "" : Number(e.target.value);
                setProjectValues({ ...projectValues, simulation_tenure: val });
              }}
              placeholder="Enter Simulation Tenure (e.g. 25)..."
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>CONTRACTED ENERGY @ POI (MWH)</label>
            <input
              type="number"
              value={projectValues.energy_poi_mwh}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  energy_poi_mwh: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="Enter energy @ POI..."
            />
          </div>
        </div>

        {/* Manual Augmentation Schedule Editor */}
        {projectValues.simulation_type === "closed_loop" && (
          <div className="border-t border-slate-100 pt-5 space-y-4">
            <h4 className="font-bold text-slate-800 text-[14px] flex items-center gap-1.5">
              <SettingOutlined className="text-[#0052CC]" />
              Manual Augmentation Schedule Editor
            </h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Specify the project years and the number of containers to be purchased in those years.
            </p>

            <div className="space-y-3">
              {(projectValues.purchase_years || []).map((item, idx) => {
                const currentYear = typeof item === "object" && item !== null ? item.year : item;
                const currentContainers = typeof item === "object" && item !== null ? item.containers : "";

                return (
                  <div key={idx} className="flex items-end gap-4 py-2">
                    <div className="flex-1 flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Project Year</label>
                      <input
                        type="number"
                        min="1"
                        max={projectValues.simulation_tenure || 50}
                        value={currentYear ?? ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? "" : Number(e.target.value);
                          const updated = [...(projectValues.purchase_years || [])];
                          if (typeof updated[idx] === "object" && updated[idx] !== null) {
                            updated[idx] = { ...updated[idx], year: val };
                          } else {
                            updated[idx] = { year: val, containers: 1 };
                          }
                          handlePurchaseYearsChange(updated);
                        }}
                        placeholder="Year e.g. 4"
                        className="h-9 px-3 border border-slate-200 rounded-md text-xs w-full bg-white"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Container Quantity</label>
                      <input
                        type="number"
                        min="1"
                        value={currentContainers ?? ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? "" : Number(e.target.value);
                          const updated = [...(projectValues.purchase_years || [])];
                          if (typeof updated[idx] === "object" && updated[idx] !== null) {
                            updated[idx] = { ...updated[idx], containers: val };
                          } else {
                            updated[idx] = { year: 1, containers: val };
                          }
                          handlePurchaseYearsChange(updated);
                        }}
                        placeholder="Containers e.g. 2"
                        className="h-9 px-3 border border-slate-200 rounded-md text-xs w-full bg-white"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = (projectValues.purchase_years || []).filter((_, i) => i !== idx);
                        handlePurchaseYearsChange(updated);
                      }}
                      className="h-9 px-3 bg-red-50 text-red-600 rounded-md text-xs font-semibold hover:bg-red-100 transition-colors flex items-center gap-1 cursor-pointer"
                    >
                      <DeleteOutlined /> Remove
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => {
                const current = projectValues.purchase_years || [];
                let nextYear = 1;
                if (current.length > 0) {
                  const lastItem = current[current.length - 1];
                  const lastYear = typeof lastItem === "object" && lastItem !== null ? lastItem.year : lastItem;
                  if (typeof lastYear === "number" && !isNaN(lastYear)) {
                    nextYear = lastYear + 1;
                  }
                }
                handlePurchaseYearsChange([...current, { year: nextYear, containers: 1 }]);
              }}
              className="h-9 px-4 border border-dashed border-[#0052cc] text-[#0052cc] hover:bg-blue-50/50 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer bg-white"
            >
              <PlusOutlined /> Add Augmentation
            </button>

            {step1Touched.purchase_years && step1Errors.purchase_years && (
              <span className="text-[10px] text-red-500 font-semibold mt-1 block">
                {step1Errors.purchase_years}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Auto Augmentation Parameters */}
      {projectValues.simulation_type === "auto_closed_loop" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div className="flex flex-col gap-1">
            <label>MIN AUGMENTATION PERIOD (YEARS)</label>
            <input
              type="number"
              value={projectValues.min_aug_period}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  min_aug_period: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="Min aug period..."
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>MIN AUGMENTATION CONTAINER QTY (NOS)</label>
            <input
              type="number"
              value={projectValues.min_aug_container}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  min_aug_container: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="Min aug container qty..."
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>MAX STORAGE CAPACITY (%)</label>
            <input
              type="number"
              value={projectValues.max_storage_capacity}
              onChange={(e) =>
                setProjectValues({
                  ...projectValues,
                  max_storage_capacity: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              placeholder="e.g. 150"
            />
          </div>
        </div>
      )}

      {/* System Losses Settings */}
      <div className="mt-8 space-y-6 border-t border-slate-100 pt-6">
        <h4 className="form-section-title text-base font-bold text-slate-800 uppercase tracking-wider pb-1">
          System Losses Settings
        </h4>

        <div className="flex flex-col md:flex-row gap-6 mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voltage Stage 1 (kV)</label>
            <div className="flex gap-4 items-center">
              {[66, 110, 132, 220].map((val) => (
                <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="step1_tl_volt_stage_1"
                    value={val}
                    checked={(projectValues.tl_volt_stage_1 || 220) === val}
                    onChange={() => setProjectValues({ ...projectValues, tl_volt_stage_1: val })}
                    className="w-4 h-4 accent-[#0052cc]"
                  />
                  <span className="text-sm text-slate-700 font-medium">{val}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="w-px bg-slate-200 hidden md:block"></div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voltage Stage 2 (kV)</label>
            <div className="flex gap-4 items-center">
              {[220, 400].map((val) => (
                <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="step1_tl_volt_stage_2"
                    value={val}
                    checked={(projectValues.tl_volt_stage_2 || 400) === val}
                    onChange={() => setProjectValues({ ...projectValues, tl_volt_stage_2: val })}
                    className="w-4 h-4 accent-[#0052cc]"
                  />
                  <span className="text-sm text-slate-700 font-medium">{val}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Stage 1 Transmission Line Loss (%) */}
          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center gap-1.5 h-4 mb-1">
              <input
                type="checkbox"
                checked={projectValues.use_220kv_loss}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setProjectValues({
                    ...projectValues,
                    use_220kv_loss: checked,
                    transmission_line_220kv_loss: checked ? projectValues.transmission_line_220kv_loss : "",
                  });
                }}
                className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
              />
              <span className="text-xs font-semibold select-none text-[#0c2340]">
                {projectValues.tl_volt_stage_1 || 220}kV Transmission Line Loss (%)
              </span>
            </div>
            <input
              type="number"
              step="0.01"
              disabled={!projectValues.use_220kv_loss}
              value={projectValues.transmission_line_220kv_loss}
              onChange={(e) =>
                setProjectValues({ ...projectValues, transmission_line_220kv_loss: e.target.value })
              }
              placeholder="0.50"
              className={
                !projectValues.use_220kv_loss
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                  : ""
              }
            />
          </div>

          {/* Stage 1 Transformer Loss (%) */}
          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center gap-1.5 h-4 mb-1">
              <input
                type="checkbox"
                checked={projectValues.use_transformer_220_loss}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setProjectValues({
                    ...projectValues,
                    use_transformer_220_loss: checked,
                    transformer_220kv_loss: checked ? projectValues.transformer_220kv_loss : "",
                  });
                }}
                className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
              />
              <span className="text-xs font-semibold select-none text-[#0c2340]">
                {projectValues.tl_volt_stage_1 || 220}/33kV Transformer Loss (%)
              </span>
            </div>
            <input
              type="number"
              step="0.01"
              disabled={!projectValues.use_transformer_220_loss}
              value={projectValues.transformer_220kv_loss}
              onChange={(e) => setProjectValues({ ...projectValues, transformer_220kv_loss: e.target.value })}
              placeholder="0.15"
              className={
                !projectValues.use_transformer_220_loss
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                  : ""
              }
            />
          </div>

          {/* Stage 2 Transmission Line Loss (%) */}
          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center gap-1.5 h-4 mb-1">
              <input
                type="checkbox"
                checked={projectValues.use_400kv_loss}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setProjectValues({
                    ...projectValues,
                    use_400kv_loss: checked,
                    transmission_line_400kv_loss: checked ? projectValues.transmission_line_400kv_loss : "",
                  });
                }}
                className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
              />
              <span className="text-xs font-semibold select-none text-[#0c2340]">
                {projectValues.tl_volt_stage_2 || 400}kV Transmission Line Loss (%)
              </span>
            </div>
            <input
              type="number"
              step="0.01"
              disabled={!projectValues.use_400kv_loss}
              value={projectValues.transmission_line_400kv_loss}
              onChange={(e) =>
                setProjectValues({ ...projectValues, transmission_line_400kv_loss: e.target.value })
              }
              placeholder="0.50"
              className={
                !projectValues.use_400kv_loss
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                  : ""
              }
            />
          </div>

          {/* Stage 2 Transformer Loss (%) */}
          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center gap-1.5 h-4 mb-1">
              <input
                type="checkbox"
                checked={projectValues.use_transformer_400_loss}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setProjectValues({
                    ...projectValues,
                    use_transformer_400_loss: checked,
                    transformer_400kv_loss: checked ? projectValues.transformer_400kv_loss : "",
                  });
                }}
                className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
              />
              <span className="text-xs font-semibold select-none text-[#0c2340]">
                {projectValues.tl_volt_stage_2 || 400}/{projectValues.tl_volt_stage_1 || 220}kV Transformer Loss (%)
              </span>
            </div>
            <input
              type="number"
              step="0.01"
              disabled={!projectValues.use_transformer_400_loss}
              value={projectValues.transformer_400kv_loss}
              onChange={(e) => setProjectValues({ ...projectValues, transformer_400kv_loss: e.target.value })}
              placeholder="0.15"
              className={
                !projectValues.use_transformer_400_loss
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                  : ""
              }
            />
          </div>

          {/* Cable & Equipment Losses */}
          <div className="flex flex-col gap-1">
            <label>SOLAR AUX POWER (KW)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.solar_aux_power}
              onChange={(e) => setProjectValues({ ...projectValues, solar_aux_power: e.target.value })}
              placeholder="1.0"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>HT AC CABLE LOSS (%)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.ht_ac_cable_loss}
              onChange={(e) => setProjectValues({ ...projectValues, ht_ac_cable_loss: e.target.value })}
              placeholder="0.10"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>IDT LOSS (%)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.idt_loss}
              onChange={(e) => setProjectValues({ ...projectValues, idt_loss: e.target.value })}
              placeholder="1.00"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>LT AC CABLE LOSS (%)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.lt_ac_cable_loss}
              onChange={(e) => setProjectValues({ ...projectValues, lt_ac_cable_loss: e.target.value })}
              placeholder="0.33"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>PCS LOSS (%)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.pcs_loss}
              onChange={(e) => setProjectValues({ ...projectValues, pcs_loss: e.target.value })}
              placeholder="1.50"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>LT DC CABLE LOSS (%)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.lt_dc_cable_loss}
              onChange={(e) => setProjectValues({ ...projectValues, lt_dc_cable_loss: e.target.value })}
              placeholder="0.12"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label>BESS PLANT AVAILABILITY (%)</label>
            <input
              type="number"
              step="0.01"
              value={projectValues.bess_plant_unavailability}
              onChange={(e) =>
                setProjectValues({ ...projectValues, bess_plant_unavailability: e.target.value })
              }
              placeholder="1.50"
            />
          </div>
        </div>
      </div>

      {/* Step 1 BESS Sizing Preview Section */}
      {showPreview && previewData && (
        <div className="mt-8 pt-6 border-t border-slate-200 space-y-6 animate-fade-in bg-slate-50/50 p-5 rounded-2xl border border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider">
                  BESS Sizing Graphs Preview
                </h4>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Evaluating configuration based on {Number(projectValues.energy_poi_mwh || 0).toFixed(1)} MWh POI
                Contract Target across {projectValues.simulation_tenure || 25} years.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="h-8 px-3 text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:bg-slate-100 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer self-start sm:self-auto shadow-sm"
            >
              <EyeInvisibleOutlined /> Hide Preview
            </button>
          </div>

          {/* Breach Alert if any */}
          {previewData.summary?.years_below_contract &&
            previewData.summary.years_below_contract.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs font-semibold flex flex-col gap-1 text-red-700 shadow-sm">
                <span className="font-bold flex items-center gap-1.5">
                  <WarningOutlined className="text-red-500" />
                  Diagnostics Alert: Contract Breach Detected
                </span>
                <span className="text-red-600 font-medium">
                  POI delivered energy falls below contracted target in years:{" "}
                  {previewData.summary.years_below_contract.map((yr) => `Year ${yr}`).join(", ")}
                </span>
              </div>
            )}

          {/* Graphs Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="flex flex-col bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <PoiDeliveredChart
                data={previewData.year_outputs}
                targetVal={parseFloat(projectValues.energy_poi_mwh || 0)}
              />
            </div>
            <div className="flex flex-col bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <AugmentationChart data={previewData.year_outputs} />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-slate-100 mt-8 gap-4">
        <button
          type="button"
          onClick={handlePreviewSizing}
          disabled={previewLoading || submitting}
          className="h-10 px-5 border border-sky-500 text-sky-700 bg-sky-50/60 hover:bg-sky-100 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {previewLoading ? (
            <>
              <LoadingOutlined className="animate-spin" /> Simulating Sizing...
            </>
          ) : (
            <>
              <EyeOutlined /> Preview BESS Results
            </>
          )}
        </button>

        <button type="submit" disabled={submitting} className="bess-btn-primary">
          {submitting ? (
            <>
              <LoadingOutlined className="animate-spin" /> Saving...
            </>
          ) : (
            <>Save & Continue →</>
          )}
        </button>
      </div>
    </form>
  );
}
